/**
 * Audit Log Panel — flag-context driven (receives `flagKey`, no internal dropdown).
 */

import { useEffect, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Clock, User } from 'lucide-react';
import { useI18n } from '@/i18n';
import { parseBackendDateTime } from '@/lib/format/datetime';
import { useAuditLogs } from '../hooks/useFeatureFlags';

const PAGE_SIZE = 20;

interface AuditLogPanelProps {
  flagKey: string;
}

const ACTION_BADGE: Record<string, string> = {
  create: 'bg-green-100 text-green-700',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-red-100 text-red-700',
  state_change: 'bg-purple-100 text-purple-700',
  override_set: 'bg-orange-100 text-orange-700',
  override_remove: 'bg-yellow-100 text-yellow-800',
  migrate: 'bg-gray-100 text-gray-700',
};

export function AuditLogPanel({ flagKey }: AuditLogPanelProps) {
  const { t, language } = useI18n();
  const [page, setPage] = useState(0);

  // Reset to page 1 when switching flags, else a deep page from the previous flag
  // requests a high offset and shows an empty table for a flag with fewer entries.
  useEffect(() => setPage(0), [flagKey]);

  const { data, isLoading, error } = useAuditLogs(flagKey, PAGE_SIZE, page * PAGE_SIZE);

  const logs = data ?? [];
  const hasMore = logs.length === PAGE_SIZE;

  const formatDate = (isoString: string) =>
    new Intl.DateTimeFormat(language === 'zh' ? 'zh-CN' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(parseBackendDateTime(isoString));

  const getActionBadge = (action: string) => ACTION_BADGE[action] ?? 'bg-gray-100 text-gray-700';

  const getActionLabel = (action: string) => {
    const key = `featureFlags.auditLog.actions.${action}` as const;
    const label = t(key as Parameters<typeof t>[0]);
    return label !== key ? label : action;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-medium text-gray-900">{t('featureFlags.auditLog.title')}</h3>
        <p className="text-sm text-gray-500">{t('featureFlags.auditLog.description')}</p>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">{t('featureFlags.auditLog.errorLoading')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Empty */}
      {!isLoading && !error && logs.length === 0 && page === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Clock className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>{t('featureFlags.auditLog.noLogs')}</p>
          <p className="text-sm mt-1">{t('featureFlags.auditLog.noLogsHint')}</p>
        </div>
      )}

      {/* Logs */}
      {!isLoading && !error && logs.length > 0 && (
        <>
          <div className="space-y-3">
            {logs.map((log, idx) => (
              <div
                key={`${log.created_at}-${log.action}-${idx}`}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${getActionBadge(log.action)}`}>
                      {getActionLabel(log.action)}
                    </span>
                    {log.reason && <p className="text-sm text-gray-600">{log.reason}</p>}
                  </div>
                  <div className="text-right text-xs text-gray-500">
                    <div className="flex items-center gap-1 justify-end">
                      <User className="w-3 h-3" />
                      <span className="font-mono">{log.changed_by}</span>
                    </div>
                    <div className="flex items-center gap-1 justify-end mt-1">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(log.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded ${page === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              <ChevronLeft className="w-4 h-4" />
              {t('common.previous')}
            </button>
            <span className="text-sm text-gray-500">
              {t('featureFlags.auditLog.page', { page: String(page + 1) })}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded ${!hasMore ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              {t('common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
